import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  getPartnerNarrativeColor,
  shortenNarrativeLabel,
  buildChartTheme,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const partnerSplitLine = {
  lineStyle: { type: 'dashed', color: 'rgba(148, 163, 184, 0.32)', width: 1 },
};

const PartnerStackedBarChart = ({
  data,
  yAxisLabel = 'Narrative Runs',
  renderZeroWhenEmpty = false,
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);

  const hasData = !!(data?.xData?.length && data?.series?.length);
  if (!hasData && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }
  const normalizedData = hasData
    ? data
    : {
        xData: [''],
        series: [{ name: yAxisLabel, data: [0] }],
      };

  const options = {
    animation: false,
    grid: {
      ...theme.gridWithLegend,
      bottom: isLaptopCompact ? 88 : 98,
    },
    tooltip: {
      ...theme.tooltipBase,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    },
    legend: {
      ...theme.legendBase,
      type: 'scroll',
      bottom: 0,
      left: 'center',
      icon: 'roundRect',
      itemGap: isLaptopCompact ? 12 : 14,
      formatter: (name) => truncateChartLabel(name, 18),
    },
    xAxis: {
      type: 'category',
      data: normalizedData.xData,
      axisLabel: {
        ...theme.axisLabel,
        rotate: normalizedData.xData.length > 4 ? 20 : 0,
        formatter: (value) => truncateChartLabel(value, 16),
        margin: 14,
      },
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      nameTextStyle: theme.axisNameStyle,
      axisLabel: theme.axisLabel,
      min: 0,
      splitLine: partnerSplitLine,
    },
    series: normalizedData.series.map((s, idx) => ({
      name: shortenNarrativeLabel(s.name),
      type: 'bar',
      stack: 'total',
      barMaxWidth: 48,
      data: s.data,
      itemStyle: { color: getPartnerNarrativeColor(s.name, idx) },
    })),
  };

  return (
    <ReactECharts
      option={options}
      style={{ height: `${isLaptopCompact ? 320 : 340}px`, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default PartnerStackedBarChart;
